import PropTypes from 'prop-types'
import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { TrashIcon } from '@heroicons/react/20/solid'
import { uploadData } from 'aws-amplify/storage'
import { generateUniqueS3Key } from 'utils/common'
import { ImageDisplay } from './ImageDisplay'

FileAttachment.propTypes = {
    bucketAccessLevel: PropTypes.string,
    uploadLocation: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    attachments: PropTypes.array.isRequired,
    isRequired: PropTypes.bool,
    maxSize: PropTypes.number,
    maxLength: PropTypes.number,
    fileSupported: PropTypes.array,
    onChange: PropTypes.func,
    file_index: PropTypes.number,
    height: PropTypes.number,
    recommendedSize: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    className: PropTypes.string,
    isDisabled: PropTypes.bool,
}

export function FileAttachment(props) {
    const {
        bucketAccessLevel = 'public',
        uploadLocation,
        title,
        attachments,
        isRequired,
        maxSize = 2,
        maxLength = 1,
        fileSupported = ['png', 'jpeg', 'jpg', 'gif'],
        onChange,
        file_index = 0,
        height = 200,
        recommendedSize,
        id = 'dropzone-file',
        name = 'dropzone-file',
        className,
        isDisabled,
    } = props
    const { t } = useTranslation()
    const fileInputRef = useRef(null)
    const [fileUploadError, setFileUploadError] = useState(null)
    const [isUploading, setIsUploading] = useState(false)
    const maxSizeBytes = maxSize * 1024 * 1024 // 2 MB in bytes

    const handleFileChange = event => {
        const fileList = event.target.files
        uploadDocument(Array.from(fileList))
    }

    const handleDrop = event => {
        event.preventDefault()
        setFileUploadError(null)
        const fileList = event.dataTransfer.files
        uploadDocument(Array.from(fileList))
    }

    const uploadDocument = async files => {
        setIsUploading(true)
        setFileUploadError(null)
        console.log(files)
        
        // Check file count limit
        if (files.length > maxLength || attachments.length + files.length > maxLength) {
            setFileUploadError(t(`You can only upload a maximum of ${maxLength} files.`))
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
            setIsUploading(false)
            return
        }

        let errorMessages = []
        let validFiles = []

        for (const file of files) {
            const fileName = file.name || file.fileName
            const fileSize = file.size || file.fileSize
            const fileExtension = fileName.split('.').pop().toLowerCase()

            // Check file size
            if (fileSize > maxSizeBytes) {
                errorMessages.push(`${fileName}: File size exceeds ${maxSize}MB limit`)
                continue
            }

            // Check file type
            if (!fileSupported.includes(fileExtension)) {
                errorMessages.push(`${fileName}: File type not supported. Allowed types: ${fileSupported.join(', ')}`)
                continue
            }

            validFiles.push(file)
        }

        // Show specific error messages for invalid files
        if (errorMessages.length > 0) {
            setFileUploadError(errorMessages.join('; '))
        }

        // Upload valid files
        for (const file of validFiles) {
            const fileName = file.name || file.fileName
            const fileSize = file.size || file.fileSize
            const s3Key = generateUniqueS3Key(uploadLocation, fileName)
            
            try {
                const result = await uploadData({
                    path: s3Key,
                    data: file,
                    options: {
                        accessLevel: bucketAccessLevel,
                    },
                }).result

                let newAttachment = {
                    s3_key: s3Key,
                    file_name: fileName,
                    mime_type: file.type,
                    size: fileSize,
                    file_data: file,
                }

                const newAttachments = [...attachments, newAttachment]
                if (onChange) {
                    onChange(newAttachments, file_index)
                }
            } catch (err) {
                console.error(err)
                setFileUploadError(prev => 
                    prev ? `${prev}; ${fileName}: Upload failed` : `${fileName}: Upload failed`
                )
            }
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
        setIsUploading(false)
    }

    const handleDelete = index => {
        const newAttachments = attachments.filter((_, i) => i !== index)
        if (onChange) {
            onChange(newAttachments, file_index)
        }

    }

    return (
        <>
            <div><label htmlFor={id} className="block text-sm font-medium leading-6 text-gray-900">
                {title}
                {isRequired && <span className="text-red">*</span>}
            </label></div>
            {!isDisabled && (
                <div className="sm:col-span-full">

                    <label htmlFor={`${id}-info`} className="block text-sm font-small leading-6 text-gray-500">
                        {t(`Max Allowed size: ${maxSize}MB | Supported file type: ${fileSupported.join(', ')}`)}
                        {recommendedSize ? (
                            <span className="text-gray-800 font-bold"> {t(`| Recommended  size: ${recommendedSize}`)}</span>
                        ) : null}
                    </label>
                    <div
                        className="relative mt-2"
                        onDrop={handleDrop}
                        onDragOver={event => {
                            event.preventDefault()
                            event.dataTransfer.dropEffect = 'copy'
                        }}
                    >
                        <label
                            htmlFor={id}
                            className={`flex flex-col items-center justify-center w-full h-[${height}px] border border-1 border-black/30 border-dashed rounded-lg cursor-pointer bg-white ${className}`}
                        >
                            <div className={`flex flex-col items-center justify-center pt-5 pb-6`}>
                                <svg
                                    className="w-8 h-8 mb-4 text-black/50"
                                    aria-hidden="true"
                                    fill="none"
                                    viewBox="0 0 20 16"
                                >
                                    <path
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                                    />
                                </svg>
                                <p className="mb-2 text-sm">
                                    <span className="font-semibold  text-secondary">{t('Click to upload')}</span>
                                    <span> {t('or')} </span>
                                    {t('Drag and drop')}
                                </p>
                                <p className="text-xs leading-5 text-gray-600">
                                    {fileSupported.join(', ')} {t('upto')} {maxSize}MB
                                </p>
                            </div>
                            <input
                                ref={fileInputRef}
                                id={id}
                                name={name}
                                type="file"
                                className="hidden"
                                multiple
                                onChange={handleFileChange}
                                onInput={event => {
                                    setFileUploadError(null)
                                }}
                            />
                        </label>
                    </div>
                    {fileUploadError && <p className="text-error text-sm font-medium mt-1">{fileUploadError}</p>}
                </div>
            )}

            {isUploading && (
                <div className="sm:col-span-full mt-2">
                    <p className="text-sm font-medium text-gray-900">{t('Uploading Please wait ...')}</p>
                </div>
            )}

            {attachments.length > 0 && (
                <div className="sm:col-span-full">
                    <ul
                        role="list"
                        className="grid grid-cols-3 gap-x-2 gap-y-5 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3 xl:gap-x-2 mt-3"
                    >
                        {attachments.map((file, index) => (
                            <li key={file.s3_key} className="relative group w-auto overflow">
                                <div className="h-auto block w-auto overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100">
                                    <ImageDisplay
                                        s3Key={file.s3_key}
                                        file={file.file_data}
                                        classStyle="object-cover group-hover:opacity-75 w-full"
                                    />
                                </div>
                                <div className="w-auto flex justify-between items-center px-0 py-2">
                                    <p className="pointer-events-none block truncate text-sm font-medium text-gray-900 group-hover:text-primary">
                                        {file.file_name}
                                    </p>
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-x-1.5 rounded-md text-sm text-red"
                                        onClick={() => handleDelete(index)}
                                    >
                                        <TrashIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 hover:text-error" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    )
}